import React, { useState } from 'react';
import { Row, Col, Button, Input, Typography, message } from 'antd';
import { SafetyOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import withUnAuth from './withUnAuth';
import SignInForm from '../components/Forms/Auth/SignIn';
import TwoFactorEnroll from '../components/TwoFactorEnroll';
import { useDispatch } from 'react-redux';
import { doSignIn } from '../modules/auth/stores/thunk';
import { setSession } from '../modules/auth/stores/slice';
import { twoFactorVerify } from '../modules/auth/api';
import { AuthContainer } from '../modules/auth/containers';
import { tenantConfig } from '../config/tenantConfig';

const SignInPage = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation('auth');
  // OVO-159 — login is a small state machine now:
  //   credentials → (enrolled) code step → session
  //   credentials → (enforcement, un-enrolled) forced enrollment → session
  const [stage, setStage] = useState('credentials');
  const [preAuthToken, setPreAuthToken] = useState(null);
  const [setupToken, setSetupToken] = useState(null);
  const [code, setCode] = useState('');
  const [verifying, setVerifying] = useState(false);

  const onLogin = async (params) => {
    try {
      const res = await dispatch(doSignIn(params)).unwrap();
      if (res?.two_factor_required) {
        setPreAuthToken(res.pre_auth_token);
        setStage('code');
      } else if (res?.two_factor_setup_required) {
        setSetupToken(res.setup_token);
        setStage('setup');
      }
      // Plain access_token → the doSignIn.fulfilled reducer already
      // installed the session; the route guard takes over.
    } catch {
      // Error toast handled by the global error flow.
    }
  };

  const onVerify = async () => {
    if (!code.trim()) return;
    setVerifying(true);
    const { response, error } = await twoFactorVerify(preAuthToken, code.trim());
    setVerifying(false);
    if (error) {
      message.error(
        error?.status === 401
          ? t('twoFactorInvalidCode', 'Invalid code — try again.')
          : error?.message || 'Verification failed',
      );
      if (error?.message?.includes('expired')) setStage('credentials');
      return;
    }
    dispatch(setSession(response));
  };

  return (
    <div className="SignInPage">
      <Row>
        <Col xs={0} sm={0} md={{ span: 12 }}>
          <div
            className="LeftImg"
            style={{
              backgroundImage: `url('${tenantConfig.loginBackground}')`,
              backgroundSize: 'cover',
            }}
          />
        </Col>
        <Col xs={24} sm={24} md={{ span: 12 }}>
          {stage === 'credentials' && <SignInForm onSubmit={onLogin} />}
          {stage === 'code' && (
            <div className="SignInForm" style={{ maxWidth: 360, margin: '120px auto 0' }}>
              <Typography.Title level={4}>{t('twoFactorTitle', 'Two-factor authentication')}</Typography.Title>
              <Typography.Paragraph type="secondary">
                {t(
                  'twoFactorCodeHint',
                  'Enter the 6-digit code from your authenticator app, or one of your recovery codes.',
                )}
              </Typography.Paragraph>
              <Input
                size="large"
                prefix={<SafetyOutlined />}
                placeholder="123 456"
                value={code}
                maxLength={12}
                onChange={(e) => setCode(e.target.value)}
                onPressEnter={onVerify}
                autoFocus
                style={{ marginBottom: 12 }}
              />
              <Button type="primary" block size="large" loading={verifying} onClick={onVerify}>
                {t('twoFactorVerify', 'Verify')}
              </Button>
              <Button type="link" block onClick={() => setStage('credentials')} style={{ marginTop: 8 }}>
                {t('twoFactorBack', 'Back to login')}
              </Button>
            </div>
          )}
          {stage === 'setup' && (
            <div className="SignInForm" style={{ maxWidth: 380, margin: '80px auto 0' }}>
              <Typography.Title level={4}>
                {t('twoFactorSetupRequired', 'Set up two-factor authentication')}
              </Typography.Title>
              <Typography.Paragraph type="secondary">
                {t(
                  'twoFactorSetupRequiredHint',
                  'Your organization requires two-factor authentication for admin accounts.',
                )}
              </Typography.Paragraph>
              <TwoFactorEnroll token={setupToken} onDone={(enabled) => dispatch(setSession(enabled))} />
            </div>
          )}
        </Col>
      </Row>
    </div>
  );
};

export default AuthContainer(withUnAuth(SignInPage));
